import React, { useState, useEffect, useMemo } from 'react';
import { MenuItem } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Close, ChevronLeft, ChevronRight } from '@mui/icons-material';

import { getLockerArea, getLockers } from '../../../../../api/ticket';

import {
  StyledModal,
  ModalContainer,
  ModalHeader,
  ModalHeaderTitle,
  CloseIconWrapper,
  ModalContent,
  ModalFooter,
  DropdownWrapper,
  LockerGridContainer,
  LockerGridWrapper,
  LockerRow,
  LockerItem,
  LegendWrapper,
  LegendItem,
  LegendColor,
  LegendText,
  StyledSelect,
  ConfirmButton,
  CancelButton,
  NavigationButton,
} from './LockerSelectModalStyled';

// 상수 정의
const COLUMNS_PER_ROW = 10; // 한 행당 락커 수
const ROWS_PER_PAGE = 10; // 한 페이지당 행 수
const LOCKERS_PER_PAGE = COLUMNS_PER_ROW * ROWS_PER_PAGE; // 한 페이지당 락커 수 (100개)

const LockerSelectModal = ({
  open,
  onClose,
  onSelect,
  initialLocker,
  memberId,
  // 디자인 최종안 06 — 용도별 제목/버튼과 출발지 → 도착지 요약
  title,
  confirmText,
  // 현재 자리에 이용권이 여러 개일 때 함께 이관할 이용권 체크박스
  // [{ memberLockerPassId, productName, startDate, expireDate }]
  passOptions = [],
  // 이동 주체 이용권 — 항상 포함(체크 해제 불가)
  primaryPassId,
}) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);

  const [selectedLocker, setSelectedLocker] = useState(null);
  const [selectedAreaId, setSelectedAreaId] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedPassIds, setSelectedPassIds] = useState([]);

  // 락커 구역 목록 조회
  const { data: lockerAreas = [] } = useQuery({
    queryKey: ['lockerAreas', user?.branch?.branchId],
    queryFn: () => getLockerArea(user?.branch?.branchId, user?.token),
    enabled: !!user?.branch?.branchId && !!user?.token && open,
  });

  // 락커 목록 조회
  const { data: lockers = [] } = useQuery({
    queryKey: ['lockers', user?.branch?.branchId],
    queryFn: () => getLockers(user?.branch?.branchId, user?.token),
    enabled: !!user?.branch?.branchId && !!user?.token && open,
  });

  // 모달 열릴 때 초기값 설정
  useEffect(() => {
    if (open) {
      if (initialLocker) {
        // 현재 자리는 '현재' 마커로만 표시 — 목적지는 새로 고른다
        setSelectedLocker(null);
        // lockerAreaId가 있으면 사용
        if (initialLocker.lockerAreaId) {
          setSelectedAreaId(initialLocker.lockerAreaId);
        } else if (initialLocker.lockerId && lockers?.length > 0) {
          // lockerId로 락커 목록에서 찾아서 lockerAreaId 가져오기
          const matchedLocker = lockers.find(locker => locker.lockerId === initialLocker.lockerId);
          if (matchedLocker) {
            setSelectedAreaId(matchedLocker.lockerAreaId);
          }
        }
      } else if (lockerAreas?.length > 0) {
        setSelectedAreaId(lockerAreas[0].id);
      }
      setCurrentPage(0);
      setSelectedPassIds(primaryPassId ? [primaryPassId] : []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lockerAreas, lockers, initialLocker]);

  // 모달 닫힐 때 내부 상태 초기화
  useEffect(() => {
    if (!open) {
      setSelectedAreaId('');
      setSelectedLocker(null);
      setCurrentPage(0);
    }
  }, [open]);

  // 선택된 구역의 락커만 필터링
  const filteredLockers = useMemo(() => {
    return selectedAreaId ? lockers.filter(locker => locker.lockerAreaId === selectedAreaId) : [];
  }, [selectedAreaId, lockers]);

  // 총 페이지 수 계산
  const totalPages = useMemo(() => {
    return Math.ceil(filteredLockers.length / LOCKERS_PER_PAGE);
  }, [filteredLockers]);

  // 현재 페이지의 락커들을 행 단위로 그룹화
  const currentPageLockers = useMemo(() => {
    const startIndex = currentPage * LOCKERS_PER_PAGE;
    const endIndex = startIndex + LOCKERS_PER_PAGE;
    const pageLockers = filteredLockers.slice(startIndex, endIndex);

    // 행 단위로 그룹화 (10개씩)
    const rows = [];
    for (let i = 0; i < pageLockers.length; i += COLUMNS_PER_ROW) {
      rows.push(pageLockers.slice(i, i + COLUMNS_PER_ROW));
    }
    return rows;
  }, [filteredLockers, currentPage]);

  // 락커가 사용 중인지 확인
  const isLockerInUse = locker => {
    return !!locker.memberId || !!locker.unregisteredMemberName;
  };

  // 본인이 점유 중인 자리 — 사용 중이어도 선택 가능(연장). 타 회원 자리만 차단한다.
  const isMyLocker = locker => !!memberId && locker.memberId === memberId;

  // 락커 선택 핸들러
  const handleLockerClick = locker => {
    if (isLockerInUse(locker) && !isMyLocker(locker)) return;

    if (selectedLocker?.lockerId === locker.lockerId) {
      setSelectedLocker(null);
    } else {
      setSelectedLocker(locker);
    }
  };

  // 확인 버튼 핸들러 — onSelect 가 false 를 반환/resolve 하면(예: 겹침 406 거부)
  // 모달을 닫지 않고 유지해 자리를 다시 고를 수 있게 한다.
  const handleConfirm = async () => {
    if (selectedLocker && onSelect) {
      const result = await onSelect(selectedLocker, selectedPassIds);
      if (result === false) return;
    }
    onClose();
  };

  const togglePass = passId => {
    if (passId === primaryPassId) return;
    setSelectedPassIds(prev =>
      prev.includes(passId) ? prev.filter(id => id !== passId) : [...prev, passId]
    );
  };

  // 모달 닫기 핸들러
  const handleClose = () => {
    setSelectedLocker(null);
    setSelectedAreaId('');
    setCurrentPage(0);
    onClose();
  };

  // 구역 변경 핸들러
  const handleAreaChange = e => {
    setSelectedAreaId(e.target.value);
    setCurrentPage(0); // 구역 변경 시 첫 페이지로 이동
  };

  // 페이지 네비게이션 핸들러
  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(prev => prev + 1);
    }
  };

  return (
    <StyledModal open={open} onClose={handleClose} aria-labelledby="locker-select-modal">
      <ModalContainer>
        {/* Header */}
        <ModalHeader>
          <ModalHeaderTitle>{title || t('라커 선택')}</ModalHeaderTitle>
          <CloseIconWrapper onClick={handleClose}>
            <Close sx={{ width: '20px', height: '20px' }} />
          </CloseIconWrapper>
        </ModalHeader>

        {/* Content */}
        <ModalContent>
          {/* 락커 구역 드롭다운 */}
          <DropdownWrapper>
            <StyledSelect value={selectedAreaId} onChange={handleAreaChange} displayEmpty>
              <MenuItem value="" disabled>
                {t('락커 구역 선택')}
              </MenuItem>
              {lockerAreas?.map(area => (
                <MenuItem key={area.id} value={area.id}>
                  {area.lockerAreaName}
                </MenuItem>
              ))}
            </StyledSelect>
          </DropdownWrapper>

          {/* 범례 */}
          <LegendWrapper>
            <LegendItem>
              <LegendColor bgColor="#0F7F85" />
              <LegendText>{t('사용가능')}</LegendText>
            </LegendItem>
            <LegendItem>
              <LegendColor bgColor="#808080" />
              <LegendText>{t('사용중')}</LegendText>
            </LegendItem>
            <LegendItem>
              <LegendColor bgColor="#FFB659" />
              <LegendText>{t('선택됨')}</LegendText>
            </LegendItem>
            {memberId && (
              <LegendItem>
                <LegendColor bgColor="#5B9BD5" />
                <LegendText>{t('내 라커(연장)')}</LegendText>
              </LegendItem>
            )}
          </LegendWrapper>

          {/* 락커 그리드 (페이지네이션 포함) */}
          <LockerGridContainer>
            {/* 이전 페이지 버튼 */}
            <NavigationButton onClick={handlePrevPage} disabled={currentPage === 0 || totalPages === 0}>
              <ChevronLeft />
            </NavigationButton>

            {/* 락커 그리드 */}
            <LockerGridWrapper>
              {currentPageLockers.map((row, rowIndex) => (
                <LockerRow key={rowIndex}>
                  {row.map(locker => (
                    <LockerItem
                      key={locker.lockerId}
                      isInUse={isLockerInUse(locker) && !isMyLocker(locker)}
                      isMine={isMyLocker(locker)}
                      isCurrent={initialLocker?.lockerId === locker.lockerId}
                      isSelected={selectedLocker?.lockerId === locker.lockerId}
                      onClick={() => handleLockerClick(locker)}
                      disabled={
                        initialLocker?.lockerId === locker.lockerId ||
                        (isLockerInUse(locker) && !isMyLocker(locker))
                      }>
                      {initialLocker?.lockerId === locker.lockerId ? (
                        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
                          {locker.lockerNumber}
                          <span style={{ fontSize: 8, fontWeight: 700 }}>{t('현재')}</span>
                        </span>
                      ) : (
                        locker.lockerNumber
                      )}
                    </LockerItem>
                  ))}
                </LockerRow>
              ))}
            </LockerGridWrapper>

            {/* 다음 페이지 버튼 */}
            <NavigationButton onClick={handleNextPage} disabled={currentPage >= totalPages - 1 || totalPages === 0}>
              <ChevronRight />
            </NavigationButton>
          </LockerGridContainer>

          {/* 함께 이동할 이용권 — 자리에 이용권이 여러 개일 때만 표시 */}
          {passOptions.length > 1 && (
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: '#404040' }}>{t('함께 이동할 이용권')}</span>
              <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, overflow: 'hidden' }}>
                {passOptions.map((lp, idx) => {
                  const checked = selectedPassIds.includes(lp.memberLockerPassId);
                  const fixed = lp.memberLockerPassId === primaryPassId;
                  return (
                    <label
                      key={lp.memberLockerPassId}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                        borderTop: idx > 0 ? '1px solid #ececec' : 'none',
                        cursor: fixed ? 'default' : 'pointer', background: checked ? 'rgba(15,127,133,0.05)' : '#fff',
                      }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={fixed}
                        onChange={() => togglePass(lp.memberLockerPassId)}
                        style={{ width: 15, height: 15, accentColor: '#0F7F85', flexShrink: 0 }}
                      />
                      <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#262626' }}>
                        {lp.productName || t('라커 이용권')}
                      </span>
                      <span style={{ fontSize: 12, color: '#737373' }}>
                        {lp.startDate?.slice(0, 10).replaceAll('-', '.')} ~ {lp.expireDate?.slice(0, 10).replaceAll('-', '.')}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </ModalContent>

        {/* Footer */}
        <ModalFooter>
          {initialLocker?.lockerNumber != null && (
            <span
              style={{
                marginRight: 'auto', display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 14, fontWeight: 700, color: '#262626',
              }}>
              {initialLocker.lockerNumber}
              {t('번')}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#0F7F85">
                <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
              </svg>
              <span style={{ color: '#0F7F85' }}>
                {selectedLocker ? `${selectedLocker.lockerNumber}${t('번')}` : '-'}
              </span>
            </span>
          )}
          <CancelButton onClick={handleClose}>{t('취소')}</CancelButton>
          <ConfirmButton onClick={handleConfirm} disabled={!selectedLocker}>
            {confirmText || t('확인')}
          </ConfirmButton>
        </ModalFooter>
      </ModalContainer>
    </StyledModal>
  );
};

export default LockerSelectModal;
